import React, { useMemo } from "react";

/**
 * NIST-style password strength meter.
 * Rules mirror the backend `security.validate_password` (min 12 chars, deny-list).
 * Visual scoring adds bonus points for diversity, but backend does NOT require it.
 */
const BANNED = new Set([
  "password", "motdepasse", "azertyazerty", "qwertyqwerty",
  "flowganttflowgantt", "administrator", "administrateur",
  "123456789012", "111111111111",
]);

export default function PasswordStrength({ value }) {
  const { score, label, color, hints, valid } = useMemo(() => {
    const v = value || "";
    let s = 0;
    const hints = [];
    if (v.length >= 12) s += 2; else hints.push(`${12 - v.length} car. de plus`);
    if (v.length >= 16) s += 1;
    if (/[a-z]/.test(v)) s += 1;
    if (/[A-Z]/.test(v)) s += 1;
    if (/[0-9]/.test(v)) s += 1;
    if (/[^A-Za-z0-9]/.test(v)) s += 1;
    const isBanned = BANNED.has(v.toLowerCase());
    if (isBanned) {
      s = 0;
      hints.length = 0;
      hints.push("Mot de passe trop commun");
    }
    const valid = v.length >= 12 && v.length <= 128 && !isBanned;
    let label, color;
    if (v.length === 0) { label = ""; color = "bg-stone-200"; }
    else if (s <= 2) { label = "Faible"; color = "bg-red-500"; }
    else if (s <= 4) { label = "Correct"; color = "bg-amber-500"; }
    else if (s <= 5) { label = "Bon"; color = "bg-sky-500"; }
    else { label = "Excellent"; color = "bg-emerald-500"; }
    return { score: s, label, color, hints, valid };
  }, [value]);

  if (!value) return null;
  const pct = Math.min(100, (score / 7) * 100);
  return (
    <div className="space-y-1" data-testid="password-strength">
      <div className="h-1 bg-stone-200 rounded-sm overflow-hidden">
        <div
          className={`h-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono-plex">
        <span
          className={
            valid ? "text-emerald-600 font-semibold" : "text-neutral-500"
          }
        >
          {label}
        </span>
        {hints.length > 0 && (
          <span className="text-neutral-500 text-right">{hints.join(" · ")}</span>
        )}
      </div>
    </div>
  );
}
